import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { GenericSelect } from "@/styles/GenericForm";
import { SaveChangesButton, CancelButton, SmallSecondaryButton } from "@/styles/StandardButtons";
import { useUser } from "@/contexts/UserContext";
import { getApiUrl } from "@/lib/api-config";
import countries from "@/lib/countries";
import { phoneCountryCodes, DEFAULT_PHONE_ISO } from "@/lib/phone-country-codes";
import { setHasUnsavedChanges } from "@/lib/unsaved-changes";

interface AccountFormState {
  first_name: string;
  last_name: string;
  // Select's `value` must be unique per item -- several countries share
  // the same dial code (+1: US/Canada/Dominican Republic/Puerto Rico/
  // Jamaica), so the ISO code is what's selected; the dial code is
  // derived from it only when composing/parsing the stored phone string.
  phoneCountryIso: string;
  localPhone: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
}

const EMPTY_FORM: AccountFormState = {
  first_name: "",
  last_name: "",
  phoneCountryIso: DEFAULT_PHONE_ISO,
  localPhone: "",
  address: "",
  city: "",
  state: "",
  zip_code: "",
  country: "",
};

const ACCOUNT_LOAD_TIMEOUT_MS = 15000;

function dialCodeFor(iso: string): string {
  return phoneCountryCodes.find((c) => c.iso === iso)?.dialCode || "+1";
}

/** Splits a stored "+1 5551234567" phone into (phoneCountryIso, localPhone) for editing. */
function splitPhone(stored: string | null | undefined): { phoneCountryIso: string; localPhone: string } {
  if (!stored) return { phoneCountryIso: DEFAULT_PHONE_ISO, localPhone: "" };
  const trimmed = stored.trim();
  const match = phoneCountryCodes
    .slice()
    .sort((a, b) => b.dialCode.length - a.dialCode.length) // longest dial code first (avoid +1 matching +12x)
    .find((c) => trimmed.startsWith(c.dialCode));
  if (match) {
    return { phoneCountryIso: match.iso, localPhone: trimmed.slice(match.dialCode.length).trim() };
  }
  return { phoneCountryIso: DEFAULT_PHONE_ISO, localPhone: trimmed };
}

export function AccountTab() {
  const { user, companyId } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState<AccountFormState>(EMPTY_FORM);
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Captured once the real data loads, used both to detect unsaved changes
  // (same comparison GenericForm's own hasFormChanged does) and to revert
  // on Cancel -- this tab can't "navigate away" to cancel to, so Cancel
  // just resets the fields back to this snapshot.
  const initialFormRef = useRef<AccountFormState>(EMPTY_FORM);
  const isDirty = JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

  useEffect(() => {
    setHasUnsavedChanges(isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  // Clear the flag if this tab unmounts some other way than Save/Cancel
  // (e.g. a future change routes away without going through either) --
  // belt and suspenders, Save/Cancel already clear it themselves.
  useEffect(() => {
    return () => setHasUnsavedChanges(false);
  }, []);

  // One request (GET /api/account/me: the profile and the company name together, under the user's own session). It used to be two browser-side Supabase
  // queries that wait on the browser client's auth lock, which is how the tab could sit on "Loading account..." for ~30 s. Now: one call, a hard timeout,
  // and an error with Retry instead of a spinner that never ends.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  useEffect(() => {
    if (!user?.id || !companyId) return;

    const controller = new AbortController();
    let finished = false; // true once this load has produced its result (or timed out, or the tab went away): a late answer must not touch state
    const timer = setTimeout(() => {
      if (finished) return;
      finished = true;
      controller.abort();
      setLoadError("Loading your account took too long.");
      setIsLoading(false);
    }, ACCOUNT_LOAD_TIMEOUT_MS);
    (async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const response = await fetch(getApiUrl("api/account/me"), { signal: controller.signal });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result?.success) throw new Error(result?.error || "Failed to load your account");
        if (finished) return;
        const userRow = result.data;

        const { phoneCountryIso, localPhone } = splitPhone(userRow?.phone);
        const loadedForm: AccountFormState = {
          first_name: userRow?.first_name || "",
          last_name: userRow?.last_name || "",
          phoneCountryIso,
          localPhone,
          address: userRow?.address || "",
          city: userRow?.city || "",
          state: userRow?.state || "",
          zip_code: userRow?.zip_code || "",
          country: userRow?.country || "",
        };
        setForm(loadedForm);
        initialFormRef.current = loadedForm;
        setEmail(userRow?.email || user.email || "");
        setCompanyName(userRow?.company_name || "");
        finished = true;
        setIsLoading(false);
      } catch (error) {
        if (finished) return;
        finished = true;
        console.error("[Account] Failed to load profile:", error);
        setLoadError(error instanceof Error ? error.message : "Failed to load your account");
        setIsLoading(false);
      } finally {
        clearTimeout(timer);
      }
    })();

    return () => {
      finished = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [user?.id, companyId, reloadToken]);

  const updateField = <K extends keyof AccountFormState>(key: K, value: AccountFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!user?.id) return;
    setIsSaving(true);
    setSaveError(null);

    const phone = form.localPhone.trim() ? `${dialCodeFor(form.phoneCountryIso)} ${form.localPhone.trim()}` : "";

    try {
      // Self-service profile edit -- backend independently enforces that a
      // caller may only patch their OWN row this way (targetUserId must
      // equal the caller), and only this exact allow-listed field set
      // (email/company/role/etc. are silently dropped server-side even if
      // sent), see backend/modules/people/service.js's update().
      const response = await fetch(getApiUrl(`api/entities/user/${user.id}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.first_name,
          last_name: form.last_name,
          phone,
          address: form.address,
          city: form.city,
          state: form.state,
          zip_code: form.zip_code,
          country: form.country,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to update account");
      }

      initialFormRef.current = form;
    } catch (error) {
      console.error("[Account] Failed to save profile:", error);
      setSaveError(error instanceof Error ? error.message : "Failed to update account");
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel discards what was not saved (after asking, when there is something to lose) and goes back to where the user came from -- or Home when
  // Settings was the first page of this session. Nothing is saved or persisted.
  const handleCancel = () => {
    if (isDirty && !window.confirm("Are you sure you want to lose all unsaved progress?")) return;
    setForm(initialFormRef.current);
    setSaveError(null);
    setHasUnsavedChanges(false); // so leaving does not ask a second time
    if (location.key !== "default") navigate(-1);
    else navigate("/");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-gray-500 font-medium">Loading account...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <Card className="max-w-3xl">
        <CardContent className="space-y-3 py-6">
          <p className="text-sm text-red-600" role="alert">{loadError}</p>
          <SmallSecondaryButton type="button" onClick={() => setReloadToken((n) => n + 1)}>Retry</SmallSecondaryButton>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-3xl">
      <CardHeader>
        <CardTitle className="font-heading-text">Account</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="first_name">First name</Label>
            <Input id="first_name" className="mt-1.5" value={form.first_name} onChange={(e) => updateField("first_name", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="last_name">Last name</Label>
            <Input id="last_name" className="mt-1.5" value={form.last_name} onChange={(e) => updateField("last_name", e.target.value)} />
          </div>
        </div>

        <div>
          <Label>Phone number</Label>
          {/* the dial-code box is a fixed, narrow column (the field's own CSS makes a select as wide as its container, so the width goes on the wrapper) */}
          <div className="mt-1.5 flex gap-2" data-testid="phone-row">
            <div className="w-28 shrink-0" data-testid="phone-country">
              <GenericSelect
                value={form.phoneCountryIso}
                onValueChange={(v) => updateField("phoneCountryIso", v)}
                required
                contentClassName="max-h-[18.75rem] overflow-y-auto"
                // Several countries share a dial code (+1), so the trigger shows just
                // the code for the selected ISO instead of the matched item's label.
                renderValue={dialCodeFor(form.phoneCountryIso)}
                options={phoneCountryCodes.map((c) => ({ label: `${c.name} (${c.dialCode})`, value: c.iso }))}
              />
            </div>
            <div className="min-w-0 flex-1" data-testid="phone-number">
              <Input
                type="tel"
                inputMode="tel"
                aria-label="Phone number"
                placeholder="Phone number"
                value={form.localPhone}
                onChange={(e) => updateField("localPhone", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" className="mt-1.5 bg-gray-50 text-gray-500" value={email} disabled readOnly />
          <p className="mt-1 text-xs text-gray-400">Your login email can't be changed.</p>
        </div>

        <div>
          <Label htmlFor="company">Company</Label>
          <Input id="company" className="mt-1.5 bg-gray-50 text-gray-500" value={companyName} disabled readOnly />
        </div>

        <div className="pt-2 border-t border-gray-100">
          <p className="text-sm font-medium text-gray-700 mb-4">Address</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="address">Street address</Label>
              <Input id="address" className="mt-1.5" value={form.address} onChange={(e) => updateField("address", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="city">City</Label>
              <Input id="city" className="mt-1.5" value={form.city} onChange={(e) => updateField("city", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="state">State</Label>
              <Input id="state" className="mt-1.5" value={form.state} onChange={(e) => updateField("state", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="zip_code">ZIP code</Label>
              <Input id="zip_code" className="mt-1.5" value={form.zip_code} onChange={(e) => updateField("zip_code", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="country">Country</Label>
              <GenericSelect
                value={form.country}
                onValueChange={(v) => updateField("country", v)}
                required
                className="mt-1.5"
                placeholder="Select"
                contentClassName="max-h-[18.75rem] overflow-y-auto"
                options={countries.map((c) => ({ label: c, value: c }))}
              />
            </div>
          </div>
        </div>

        {saveError && <p className="text-sm text-red-600">{saveError}</p>}

        <div className="flex justify-end gap-2">
          <CancelButton onClick={handleCancel} disabled={isSaving} />
          <SaveChangesButton type="button" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save changes"}
          </SaveChangesButton>
        </div>
      </CardContent>
    </Card>
  );
}
